<div class="w-full h-full">
    @if (loading) {
        <div class="rounded-lg table-content-scroll">
            <table class="w-full bg-white border-collapse">
                <thead>
                    <tr class="bg-primary">
                        @for (col of columns; track $index) {
                            <th
                                class="px-6 py-3 text-xs font-bold tracking-wider text-left text-white uppercase border-b border-gray-200"
                                [ngStyle]="getColumnStyle(col)"
                            >
                                {{ col.label }}
                            </th>
                        }
                        @if (rowActions.length > 0) {
                            <th
                                class="px-6 py-3 text-xs font-bold tracking-wider text-left text-white uppercase border-b border-gray-200 data-table-actions-col"
                            >
                                Actions
                            </th>
                        }
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td [attr.colspan]="columns.length + (rowActions.length > 0 ? 1 : 0)" class="px-4 py-4">
                            <div class="flex items-center justify-center">
                                <lucide-icon [img]="Loader" class="w-8 h-8 text-primary animate-spin" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    } @else if (error) {
        <div
            class="flex items-center justify-center h-32 font-semibold text-red-600 bg-white border border-red-200 rounded"
        >
            Error: {{ error }}
        </div>
    } @else {
        @if (stickyHeader) {
            <!-- Split sticky mode keeps header fixed to page scroll while body keeps horizontal scroll. -->
            <div class="rounded-lg">
                <div class="app-sticky-split-header" [style.top]="'var(--sticky-header-top, 0px)'">
                    <div #headerScrollContainer class="split-header-scroll" (scroll)="syncBodyScroll($event)">
                        <table
                            #headerTable
                            class="bg-white border-collapse split-table"
                            [style.width.px]="splitTableWidth || null"
                        >
                            @if (columnWidths.length > 0) {
                                <colgroup>
                                    @for (colWidth of columnWidths; track $index) {
                                        <col [style.width.px]="colWidth" />
                                    }
                                </colgroup>
                            }
                            <thead>
                                <tr class="bg-primary">
                                    @for (col of columns; track $index) {
                                        <th
                                            class="px-6 py-3 text-xs font-bold tracking-wider text-left text-white uppercase border-b border-gray-200"
                                            [ngStyle]="getColumnStyle(col)"
                                        >
                                            {{ col.label }}
                                        </th>
                                    }
                                    @if (rowActions.length > 0) {
                                        <th
                                            class="px-6 py-3 text-xs font-bold tracking-wider text-left text-white uppercase border-b border-gray-200 data-table-actions-col"
                                        >
                                            Actions
                                        </th>
                                    }
                                </tr>
                            </thead>
                        </table>
                    </div>
                </div>

                @if (displayData.length > 0) {
                    <div #bodyScrollContainer class="table-content-scroll" (scroll)="syncHeaderScroll($event)">
                        <table
                            #bodyTable
                            class="bg-white border-collapse split-table"
                            [style.width.px]="splitTableWidth || null"
                        >
                            @if (columnWidths.length > 0) {
                                <colgroup>
                                    @for (colWidth of columnWidths; track $index) {
                                        <col [style.width.px]="colWidth" />
                                    }
                                </colgroup>
                            }
                            <tbody>
                                @for (row of displayData; let i = $index; track row) {
                                    <tr class="transition even:bg-gray-50 hover:bg-blue-50" style="font-size: 14px">
                                        @for (col of columns; track $index) {
                                            <td
                                                class="px-6 py-3 text-gray-700 whitespace-pre-wrap border-b border-gray-100"
                                                [ngStyle]="getColumnStyle(col)"
                                            >
                                                @switch (col.type || "text") {
                                                    @case ("chips") {
                                                        @if (getCellValue(col, row, i) | isArray) {
                                                            <div class="flex flex-wrap gap-1">
                                                                @for (chip of getCellValue(col, row, i); track chip) {
                                                                    <span [ngClass]="getChipClasses(col, chip, row)">
                                                                        {{ getChipDisplay(col, chip, row) }}
                                                                    </span>
                                                                }
                                                            </div>
                                                        } @else {
                                                            <span
                                                                [ngClass]="
                                                                    getChipClasses(col, getCellValue(col, row, i), row)
                                                                "
                                                            >
                                                                {{
                                                                    getChipDisplay(col, getCellValue(col, row, i), row)
                                                                }}
                                                            </span>
                                                        }
                                                    }
                                                    @case ("image") {
                                                        @if (getCellValue(col, row, i)) {
                                                            <img
                                                                [src]="getCellValue(col, row, i)"
                                                                [alt]="getImageAlt(col, row)"
                                                                [ngStyle]="{
                                                                    width: col.imageConfig?.width || '40px',
                                                                    height: col.imageConfig?.height || '40px',
                                                                }"
                                                                class="object-cover rounded"
                                                                (error)="onImageError($event, col)"
                                                            />
                                                        } @else if (col.imageConfig?.fallbackIcon) {
                                                            <div
                                                                class="flex items-center justify-center bg-gray-200 rounded"
                                                                [ngStyle]="{
                                                                    width: col.imageConfig?.width || '40px',
                                                                    height: col.imageConfig?.height || '40px',
                                                                }"
                                                            >
                                                                <lucide-icon
                                                                    [img]="col.imageConfig?.fallbackIcon"
                                                                    class="w-4 h-4 text-gray-400"
                                                                />
                                                            </div>
                                                        } @else {
                                                            <span class="text-sm text-gray-400">No image</span>
                                                        }
                                                    }
                                                    @default {
                                                        {{ getCellValue(col, row, i) }}
                                                    }
                                                }
                                            </td>
                                        }
                                        @if (rowActions.length > 0) {
                                            <td class="px-6 py-3 border-b border-gray-100 data-table-actions-col">
                                                <div class="flex items-center gap-2">
                                                    @for (action of rowActions; track action.id) {
                                                        @if (shouldShowAction(action, row)) {
                                                            <button
                                                                type="button"
                                                                [title]="
                                                                    action.tooltip
                                                                        ? typeof action.tooltip === 'function'
                                                                            ? action.tooltip(row)
                                                                            : action.tooltip
                                                                        : null
                                                                "
                                                                class="px-2 py-1 text-xs"
                                                                [ngClass]="getButtonClass(action, row)"
                                                                (click)="onRowAction(action, row, i)"
                                                                [disabled]="shouldDisableAction(action, row)"
                                                            >
                                                                @if (action.icon) {
                                                                    <lucide-icon
                                                                        [img]="
                                                                            action.icon
                                                                                ? typeof action.icon === 'function'
                                                                                    ? action.icon(row)
                                                                                    : action.icon
                                                                                : null
                                                                        "
                                                                        class="inline w-4 h-4 align-text-bottom"
                                                                        [ngClass]="[
                                                                            getActionLabel(action, row) ? 'mr-1' : '',
                                                                            action.iconClass ? action?.iconClass : '',
                                                                        ]"
                                                                    />
                                                                }
                                                                @if (getActionLabel(action, row)) {
                                                                    {{ getActionLabel(action, row) }}
                                                                }
                                                            </button>
                                                        }
                                                    }
                                                </div>
                                            </td>
                                        }
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                } @else {
                    <div class="px-6 py-3 text-center text-gray-500 bg-white border border-gray-100 rounded-lg">
                        No data found.
                    </div>
                }
            </div>
        } @else {
            <div class="rounded-lg table-content-scroll">
                <table class="w-full bg-white border-collapse">
                    <thead>
                        <tr class="bg-primary">
                            @for (col of columns; track $index) {
                                <th
                                    class="px-6 py-3 text-xs font-bold tracking-wider text-left text-white uppercase border-b border-gray-200"
                                    [ngStyle]="getColumnStyle(col)"
                                >
                                    {{ col.label }}
                                </th>
                            }
                            @if (rowActions.length > 0) {
                                <th
                                    class="px-6 py-3 text-xs font-bold tracking-wider text-left text-white uppercase border-b border-gray-200 data-table-actions-col"
                                >
                                    Actions
                                </th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        @if (displayData.length > 0) {
                            @for (row of displayData; let i = $index; track row) {
                                <tr class="transition even:bg-gray-50 hover:bg-blue-50" style="font-size: 14px">
                                    @for (col of columns; track $index) {
                                        <td
                                            class="px-6 py-3 text-gray-700 whitespace-pre-wrap border-b border-gray-100"
                                            [ngStyle]="getColumnStyle(col)"
                                        >
                                            @switch (col.type || "text") {
                                                @case ("chips") {
                                                    @if (getCellValue(col, row, i) | isArray) {
                                                        <div class="flex flex-wrap gap-1">
                                                            @for (chip of getCellValue(col, row, i); track chip) {
                                                                <span [ngClass]="getChipClasses(col, chip, row)">
                                                                    {{ getChipDisplay(col, chip, row) }}
                                                                </span>
                                                            }
                                                        </div>
                                                    } @else {
                                                        <span
                                                            [ngClass]="
                                                                getChipClasses(col, getCellValue(col, row, i), row)
                                                            "
                                                        >
                                                            {{ getChipDisplay(col, getCellValue(col, row, i), row) }}
                                                        </span>
                                                    }
                                                }
                                                @case ("image") {
                                                    @if (getCellValue(col, row, i)) {
                                                        <img
                                                            [src]="getCellValue(col, row, i)"
                                                            [alt]="getImageAlt(col, row)"
                                                            [ngStyle]="{
                                                                width: col.imageConfig?.width || '40px',
                                                                height: col.imageConfig?.height || '40px',
                                                            }"
                                                            class="object-cover rounded"
                                                            (error)="onImageError($event, col)"
                                                        />
                                                    } @else if (col.imageConfig?.fallbackIcon) {
                                                        <div
                                                            class="flex items-center justify-center bg-gray-200 rounded"
                                                            [ngStyle]="{
                                                                width: col.imageConfig?.width || '40px',
                                                                height: col.imageConfig?.height || '40px',
                                                            }"
                                                        >
                                                            <lucide-icon
                                                                [img]="col.imageConfig?.fallbackIcon"
                                                                class="w-4 h-4 text-gray-400"
                                                            />
                                                        </div>
                                                    } @else {
                                                        <span class="text-sm text-gray-400">No image</span>
                                                    }
                                                }
                                                @default {
                                                    {{ getCellValue(col, row, i) }}
                                                }
                                            }
                                        </td>
                                    }
                                    @if (rowActions.length > 0) {
                                        <td class="px-6 py-3 border-b border-gray-100 data-table-actions-col">
                                            <div class="flex items-center gap-2">
                                                @for (action of rowActions; track action.id) {
                                                    @if (shouldShowAction(action, row)) {
                                                        <button
                                                            type="button"
                                                            [title]="
                                                                action.tooltip
                                                                    ? typeof action.tooltip === 'function'
                                                                        ? action.tooltip(row)
                                                                        : action.tooltip
                                                                    : null
                                                            "
                                                            class="px-2 py-1 text-xs"
                                                            [ngClass]="getButtonClass(action, row)"
                                                            (click)="onRowAction(action, row, i)"
                                                            [disabled]="shouldDisableAction(action, row)"
                                                        >
                                                            @if (action.icon) {
                                                                <lucide-icon
                                                                    [img]="
                                                                        action.icon
                                                                            ? typeof action.icon === 'function'
                                                                                ? action.icon(row)
                                                                                : action.icon
                                                                            : null
                                                                    "
                                                                    class="inline w-4 h-4 align-text-bottom"
                                                                    [ngClass]="[
                                                                        getActionLabel(action, row) ? 'mr-1' : '',
                                                                        action.iconClass ? action?.iconClass : '',
                                                                    ]"
                                                                />
                                                            }
                                                            @if (getActionLabel(action, row)) {
                                                                {{ getActionLabel(action, row) }}
                                                            }
                                                        </button>
                                                    }
                                                }
                                            </div>
                                        </td>
                                    }
                                </tr>
                            }
                        } @else {
                            <tr>
                                <td
                                    [attr.colspan]="columns.length + (rowActions.length > 0 ? 1 : 0)"
                                    class="px-6 py-3 text-center text-gray-500"
                                >
                                    No data found.
                                </td>
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        }
        @if (displayData.length > 0) {
            <nav class="flex flex-col justify-between gap-4 mt-6 sm:flex-row md:items-center">
                <div class="flex items-center gap-1 text-sm text-primary">
                    Showing
                    <span class="font-semibold">{{ internalMeta.from }}</span> to
                    <span class="font-semibold">{{ internalMeta.to }}</span> of
                    <span class="font-semibold">{{ internalMeta.total }}</span>
                    items
                </div>
                <ul class="flex flex-wrap items-center h-8 mt-2 -space-x-px text-sm sm:mt-0">
                    <li>
                        <button
                            type="button"
                            class="flex items-center justify-center h-8 px-3 leading-tight text-gray-500 bg-white border border-gray-300 ms-0 border-e-0 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            [disabled]="internalMeta.current_page === 1"
                            (click)="goTo(internalMeta.current_page - 1)"
                            aria-label="Previous"
                        >
                            <lucide-icon [img]="ChevronLeft" class="w-3 h-3" />
                        </button>
                    </li>
                    @for (pageNum of pages; track pageNum) {
                        <li>
                            @if (pageNum === "...") {
                                <span
                                    class="flex items-center justify-center h-8 px-3 leading-tight bg-white border border-gray-300 text-primary"
                                >
                                    {{ pageNum }}
                                </span>
                            } @else {
                                <button
                                    type="button"
                                    (click)="goToPage(pageNum)"
                                    [attr.aria-current]="internalMeta.current_page === pageNum ? 'page' : null"
                                    class="flex items-center justify-center h-8 px-3 leading-tight border border-primary text-primary hover:bg-gray-100"
                                    [ngClass]="{
                                        'z-10 text-white border-primary bg-primary font-semibold hover:bg-primary-80':
                                            internalMeta.current_page === pageNum,
                                    }"
                                >
                                    {{ pageNum }}
                                </button>
                            }
                        </li>
                    }
                    <li>
                        <button
                            type="button"
                            class="flex items-center justify-center h-8 px-3 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            [disabled]="internalMeta.current_page === internalMeta.last_page"
                            (click)="goTo(internalMeta.current_page + 1)"
                            aria-label="Next"
                        >
                            <lucide-icon [img]="ChevronRight" class="w-3 h-3" />
                        </button>
                    </li>
                </ul>
                @if (showPageInput) {
                    <div class="flex items-center gap-2">
                        <label for="pageInput" class="text-sm text-gray-700"> Go to page: </label>
                        <input
                            id="pageInput"
                            type="number"
                            min="1"
                            [max]="internalMeta.last_page"
                            class="input input--primary px-2 py-1 w-[8ch]"
                            [value]="inputPage"
                            (input)="onInputPageChange($event)"
                            (keydown.enter)="goToInputPage()"
                        />
                        <button
                            type="button"
                            class="px-3 py-1 text-xs font-semibold text-white transition rounded bg-primary"
                            (click)="goToInputPage()"
                        >
                            Go
                        </button>
                    </div>
                }
            </nav>
        }
    }
</div>
